import {AfterViewInit, Component, ContentChildren, ElementRef, OnDestroy, QueryList, Renderer2, ChangeDetectionStrategy} from '@angular/core';
import {merge, Subscription} from 'rxjs';
import {debounceTime, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../components/destroyable';
import {WidgetHeaderComponent} from '../../pages/dashboard/layout/widget/widget-header';

const WIDGET_HEADERS_HIDING_CLASS = '-widget-header-hiding';

@Component({
    selector: '[widgetHeaderHiding]',
    templateUrl: './widget-header-hiding.component.html',
    styleUrls: ['./widget-header-hiding.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WidgetHeaderHidingComponent implements OnDestroy, AfterViewInit {

    public readonly destroy$ = destroy$();

    @ContentChildren(WidgetHeaderComponent)
    private widgetHeaderComponents: QueryList<WidgetHeaderComponent>;

    private isHiding: boolean;
    private widgetTitlesSubscription: Subscription;
    private readonly resizeObserver: ResizeObserver = new ResizeObserver(() => this.updateIsHidingClass());

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2
    ) {}

    public ngOnDestroy(): void {
        this.resizeObserver.unobserve(this.elementRef.nativeElement);
    }

    public ngAfterViewInit(): void {
        this.resizeObserver.observe(this.elementRef.nativeElement);
        this.listenToWidgetSettingsUpdates();
        this.listenToWidgetHeaderListUpdate();
    }

    private listenToWidgetHeaderListUpdate(): void {
        this.widgetHeaderComponents.changes.pipe(
            takeUntil(this.destroy$),
            tap(() => {
                this.updateIsHidingClass();
                this.widgetTitlesSubscription.unsubscribe();
                this.listenToWidgetSettingsUpdates();
            })
        ).subscribe();
    }

    private listenToWidgetSettingsUpdates(): void {
        const TITLE_UPDATE_DEBOUNCE_TIME = 150;
        const widgetsTitleUpdates = this.widgetHeaderComponents.map(widgetHeader => widgetHeader.widget.title$);

        this.widgetTitlesSubscription = merge(...widgetsTitleUpdates).pipe(
            debounceTime(TITLE_UPDATE_DEBOUNCE_TIME),
            takeUntil(this.destroy$)
        ).subscribe(() => this.updateIsHidingClass());
    }

    private toggleClass(className: string, state: boolean): void {
        if (state) {
            this.renderer.addClass(this.elementRef.nativeElement, className);
        } else {
            this.renderer.removeClass(this.elementRef.nativeElement, className);
        }
    }

    private updateIsHidingClass(): void {
        if (this.isHiding) {
            this.toggleClass(WIDGET_HEADERS_HIDING_CLASS, false);
            this.isHiding = false;
        }

        const isOverflown = this.elementRef.nativeElement.scrollWidth > this.elementRef.nativeElement.offsetWidth;

        if (isOverflown !== this.isHiding) {
            this.toggleClass(WIDGET_HEADERS_HIDING_CLASS, isOverflown);
            this.isHiding = isOverflown;
        }
    }
}
